import React from "react";
import { Box, Table, TableBody, TableRow, TableCell } from "@mui/material";
import { ROW_HEIGHT, CELL_PADDING, MA_SUBTABLE_COLUMNS, TRANSLATION_Y_OFFSET, CONNECTOR_TRANSITION_BASE_DELAY } from "./MABranchConstants";
import { tokens } from "../../Common/designTokens";

const BID_SIDE_COLUMN_KEYS = ["bidQuantity", "bidPrice", "bidYield"];
const OFFER_SIDE_COLUMN_KEYS = ["offerQuantity", "offerPrice", "offerYield"];

const getCellColor = (columnKey) => {
  if (BID_SIDE_COLUMN_KEYS.includes(columnKey)) {
    return tokens.colors.buyFontColor;
  }
  if (OFFER_SIDE_COLUMN_KEYS.includes(columnKey)) {
    return tokens.colors.sellFontColor;
  }
  return tokens.colors.fontColour;
};

const formatDealerValue = (columnKey, value) => {
  if (value === undefined || value === null || value === "") {
    return "---";
  }

  if (columnKey === "bidQuantity" || columnKey === "offerQuantity") {
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? numericValue.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : String(value);
  }

  if (columnKey === "bidPrice" || columnKey === "offerPrice") {
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? numericValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : String(value);
  }

  if (columnKey === "bidYield" || columnKey === "offerYield") {
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? numericValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : String(value);
  }

  return String(value);
};

/**
 * Renders the sub-table rows for an expanded MA Inventory cusip.
 * NO header row — alignment is achieved by matching the parent BondTable's
 * column widths for the relevant keys (bidDealer … offerDealer).
 *
 * @param {Object} props
 * @param {Array}  props.dealers        - array of dealer sub-row objects
 * @param {string} props.animationState - "entering" | "entered"
 * @param {Object} props.columnWidths   - main table column widths map { key: px }
 * @param {Array}  props.parentColumns  - ordered parent table columns
 */
const MABranchSubTable = ({ dealers, animationState, columnWidths, parentColumns = [] }) => {
  const subTableColumnKeys = new Set(MA_SUBTABLE_COLUMNS.map((column) => column.key));
  const orderedColumns =
    Array.isArray(parentColumns) && parentColumns.length > 0
      ? parentColumns
          .filter((column) => subTableColumnKeys.has(column.key))
          .map((column) => ({
            ...column,
            ...MA_SUBTABLE_COLUMNS.find((subColumn) => subColumn.key === column.key),
          }))
      : MA_SUBTABLE_COLUMNS;

  // Resolve width for each sub-column from the main table's widths
  const getWidth = (key) => columnWidths[key] || 150;
  const totalWidth = orderedColumns.reduce((sum, column) => sum + getWidth(column.key), 0);

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-block",
        width: `${totalWidth}px`,
        minWidth: `${totalWidth}px`,
      }}
    >
      <Table
        size="small"
        sx={{
          width: `${totalWidth}px`,
          minWidth: `${totalWidth}px`,
          maxWidth: `${totalWidth}px`,
          backgroundColor: tokens.colors.bodyBackgroundColor,
          tableLayout: "fixed",
          borderCollapse: "collapse",
          marginLeft: 0,
          "& .MuiTableCell-root": {
            height: ROW_HEIGHT,
            padding: CELL_PADDING,
            verticalAlign: "middle",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
          "& .MuiTableRow-root": {
            opacity: animationState === "entered" ? 1 : 0,
            transition: "opacity 0.3s ease",
          },
        }}
      >
        <colgroup>
          {orderedColumns.map((column) => (
            <col key={column.key} style={{ width: `${getWidth(column.key)}px` }} />
          ))}
        </colgroup>
        <TableBody>
          {dealers.length > 0 ? (
            dealers.map((dealer, index) => (
              <TableRow
                key={index}
                sx={{
                  height: ROW_HEIGHT,
                  backgroundColor: tokens.colors.defaultGridColor,
                  opacity: animationState === "entered" ? 1 : 0,
                  transform:
                    animationState === "entered"
                      ? "translateY(0)"
                      : `translateY(${TRANSLATION_Y_OFFSET})`,
                  transition: "opacity 0.3s ease, transform 0.3s ease",
                  transitionDelay: `${CONNECTOR_TRANSITION_BASE_DELAY * index}s`,
                  "&:hover": {
                    backgroundColor: tokens.colors.defaultGridHoverColor,
                  },
                  "&:last-child .MuiTableCell-root": {
                    borderBottom: "none",
                  },
                }}
              >
                {orderedColumns.map((col) => {
                  const value = dealer[col.key];
                  const formatted = formatDealerValue(col.key, value);

                  return (
                    <TableCell
                      key={col.id}
                      sx={{
                        color: tokens.colors.fontColour,
                        fontSize: "0.875rem",
                        textAlign: col.align,
                        width: `${getWidth(col.key)}px`,
                        minWidth: `${getWidth(col.key)}px`,
                        maxWidth: `${getWidth(col.key)}px`,
                        boxSizing: "border-box",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          width: "100%",
                          color: `${getCellColor(col.key)} !important`,
                          textAlign: col.align,
                        }}
                      >
                        {formatted}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={orderedColumns.length}
                sx={{ textAlign: "center", color: tokens.colors.fontColour }}
              >
                No dealer data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default MABranchSubTable;
